import {
  Box, Fade, Menu, MenuItem, Stack, Tooltip, Typography, useMediaQuery,
} from "@mui/material";
import CustomButton from "./CustomButton";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Language as LanguageIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "../state/ThemeContext";
import { LOCALE_OPTIONS, setLocale, i18n } from "../i18n";

interface AuthLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

export default function AuthLayout({
  children,
  title,
  subtitle,
}: AuthLayoutProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("app.title");
  const { mode, toggleTheme } = useThemeMode();
  const isMobile = useMediaQuery("(max-width:640px)");
  const location = useLocation();
  const isPoliticPage = location.pathname.includes("/privacy-policy");
  const [langAnchor, setLangAnchor] = React.useState<HTMLElement | null>(null);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #18181b 0%, #27272a 40%, #18181b 100%)"
            : "linear-gradient(135deg, #f5f8fd 0%, #eef4fb 40%, #e9f1fb 100%)",
        overflow: "hidden",
        p: isMobile ? "4rem 1rem" : "5rem 1rem",
      }}
    >
      {/* Background pseudo-elements — large radial blobs */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          "&::before, &::after": {
            content: '""',
            position: "absolute",
            borderRadius: "50%",
          },
          "&::before": {
            width: { xs: "260px", md: "42vw" },
            height: { xs: "260px", md: "42vw" },
            maxWidth: "620px",
            maxHeight: "620px",
            top: "-16vw",
            left: "-10vw",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "radial-gradient(circle, rgba(113,113,122,0.15) 0%, rgba(113,113,122,0.03) 55%, transparent 72%)"
                : "radial-gradient(circle, rgba(21,101,192,0.2) 0%, rgba(21,101,192,0.04) 55%, transparent 72%)",
          },
          "&::after": {
            width: { xs: "260px", md: "42vw" },
            height: { xs: "260px", md: "42vw" },
            maxWidth: "620px",
            maxHeight: "620px",
            right: "-14vw",
            bottom: "-18vw",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "radial-gradient(circle, rgba(161,161,170,0.12) 0%, rgba(161,161,170,0.04) 58%, transparent 74%)"
                : "radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0.05) 58%, transparent 74%)",
          },
        }}
      />

      {/* Floating orbs */}
      <Box
        sx={{
          position: "absolute",
          width: 320,
          height: 320,
          top: -80,
          left: -60,
          borderRadius: "50%",
          border: "1px solid",
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(255,255,255,0.35)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 0 52px rgba(113,113,122,0.15), 0 0 34px rgba(255,255,255,0.03) inset"
              : "0 0 52px rgba(21,101,192,0.22), 0 0 34px rgba(255,255,255,0.14) inset",
          opacity: isMobile ? 0.2 : 0.28,
          filter: "blur(40px)",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "radial-gradient(circle at 58% 34%, rgba(255,255,255,0.04) 0%, rgba(113,113,122,0.03) 45%, transparent 100%)"
              : "radial-gradient(circle at 58% 34%, rgba(255,255,255,0.12) 0%, rgba(21,101,192,0.06) 45%, transparent 100%)",
          animation: "lmOrb1 12s ease-in-out infinite",
          "@keyframes lmOrb1": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "33%": { transform: "translate(20px, -15px) scale(1.04)" },
            "66%": { transform: "translate(-10px, 10px) scale(0.97)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 380,
          height: 380,
          right: -100,
          bottom: -120,
          borderRadius: "50%",
          opacity: isMobile ? 0.15 : 0.22,
          filter: "blur(40px)",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "radial-gradient(circle at 65% 35%, rgba(255,255,255,0.1), rgba(161,161,170,0.04) 64%, transparent 100%)"
              : "radial-gradient(circle at 65% 35%, rgba(255,255,255,0.32), rgba(59,130,246,0.08) 64%, transparent 100%)",
          animation: "lmOrb2 15s ease-in-out infinite",
          "@keyframes lmOrb2": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "50%": { transform: "translate(-25px, -20px) scale(1.05)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 280,
          height: 280,
          top: "45%",
          left: "55%",
          borderRadius: "50%",
          opacity: isMobile ? 0.12 : 0.18,
          filter: "blur(40px)",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "radial-gradient(circle at 40% 40%, rgba(255,255,255,0.08), rgba(113,113,122,0.04) 62%, transparent 100%)"
              : "radial-gradient(circle at 40% 40%, rgba(255,255,255,0.3), rgba(13,71,161,0.08) 62%, transparent 100%)",
          animation: "lmOrb3 18s ease-in-out infinite",
          "@keyframes lmOrb3": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "40%": { transform: "translate(15px, 12px) scale(1.03)" },
            "80%": { transform: "translate(-12px, -8px) scale(0.98)" },
          },
        }}
      />

      {/* Top right controls — language + theme */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: "absolute",
          top: isMobile ? "0.85rem" : "1.25rem",
          right: isMobile ? "0.85rem" : "1.25rem",
          zIndex: 20,
        }}
      >
          <Tooltip title={t("language.label")}>
            <span>
              <CustomButton
                variant="icon"
                icon={<LanguageIcon fontSize="small" />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={(e) => setLangAnchor(e.currentTarget)}
                aria-label={t("language.label")}
              />
            </span>
          </Tooltip>
          <Tooltip
            title={mode === "dark" ? t("app.switchToLightMode") : t("app.switchToDarkMode")}
          >
            <span>
              <CustomButton
                variant="icon"
                icon={mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={toggleTheme}
                aria-label={
                  mode === "dark" ? t("app.switchToLightMode") : t("app.switchToDarkMode")
                }
              />
            </span>
          </Tooltip>
      </Stack>

      {/* Language menu */}
      <Menu
        anchorEl={langAnchor}
        open={Boolean(langAnchor)}
        onClose={() => setLangAnchor(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {LOCALE_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === i18n.language}
            onClick={() => {
              setLocale(option.value);
              setLangAnchor(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Card */}
      <Fade in timeout={500}>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 420,
            bgcolor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.06)"
                : "rgba(21,101,192,0.08)",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 14px 40px rgba(0,0,0,0.4)"
                : "0 14px 40px rgba(15,23,42,0.14)",
            p: isMobile ? "1.5rem 1.15rem" : "2.1rem 2rem",
            display: "flex",
            flexDirection: "column",
            gap: "1.25rem",
            animation: "lmCardEnter 0.4s ease-out both",
            "@keyframes lmCardEnter": {
              from: { opacity: 0, transform: "translateY(16px) scale(0.985)" },
              to: { opacity: 1, transform: "translateY(0) scale(1)" },
            },
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #42a5f5 0%, #1565c0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              boxShadow: "0 4px 14px rgba(21, 101, 192, 0.3)",
            }}
          >
            <AdminIcon sx={{ fontSize: 28, color: "#fff" }} />
          </Box>

          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              pb: "1rem",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                letterSpacing: "-0.02em",
              }}
            >
              {resolvedTitle}
            </Typography>
          </Box>

          {/* Subtitle */}
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: -0.5 }}
            >
              {subtitle}
            </Typography>
          )}

          {/* Content */}
          <Stack spacing={1}>{children}</Stack>
        </Box>
      </Fade>

      {/* Footer */}
      <Box
        sx={{
          position: "absolute",
          bottom: "1.5rem",
          left: 0,
          right: 0,
          zIndex: 100,
          textAlign: "center",
          px: "1.5rem",
        }}
      >
        {!isPoliticPage && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: "0.6875rem",
              opacity: 0.7,
              display: "block",
              mb: 0.5,
            }}
          >
            {t("cookies.footer.text")}{" "}
            <Box
              component="span"
              sx={{
                "& a": {
                  color: "inherit",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                  "&:hover": { opacity: 0.8 },
                },
              }}
            >
              <Link to="/privacy-policy">
                {t("cookies.footer.privacyLink")}
              </Link>
            </Box>
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.6875rem", opacity: 0.7 }}
        >
          © {new Date().getFullYear()} {t("app.rights")}
        </Typography>
      </Box>
    </Box>
  );
}
